import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Virtual Staging", path: "/virtual-staging-uk" },
  { label: "3D Renders", path: "/3d-property-rendering-uk" },
  { label: "ROI Calculator", path: "/virtual-staging-roi-calculator" },
  { label: "Insights", path: "/blog" },
  { label: "FAQ", path: "/#faq" },
];

const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/ajtw0a03_TSS%20logo%20update%20transparentlight.png";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/7iqp1t8i_%20TSS%20logo%20update%20transparent%20dark%20final%20%282000%20x%201000%20px%29.png";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (link, closeMobile) => {
    if (link.path.startsWith("/#")) {
      const sectionId = link.path.replace("/#", "");
      if (closeMobile) setMobileOpen(false);
      if (location.pathname === "/") {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
      return true;
    }
    return false;
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-[#E8E8E4]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-8 lg:px-16 flex items-center justify-between h-24 lg:h-28">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3">
          <img
            src={scrolled ? LOGO_DARK : LOGO_LIGHT}
            alt="The Secret Seller"
            className="h-24 lg:h-28 w-auto transition-all duration-300"
          />
          <span className="hidden">The Secret Seller</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => {
            const isHash = link.path.startsWith("/#");
            const isActive = isHash ? false : location.pathname === link.path;
            return isHash ? (
              <a
                key={link.path}
                href={link.path}
                data-testid={`nav-link-${link.path.replace("/#", "")}`}
                onClick={(e) => { e.preventDefault(); handleNavClick(link); }}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer ${
                  scrolled ? "text-[#6B7280] hover:text-[#0D2D54]" : "text-white/70 hover:text-white"
                }`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                  isActive
                    ? (scrolled ? "text-[#0D2D54]" : "text-white")
                    : (scrolled ? "text-[#6B7280] hover:text-[#0D2D54]" : "text-white/70 hover:text-white")
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <Link
            to="/order"
            data-testid="nav-order-now-btn"
            className={`px-6 py-2.5 text-[12px] font-medium uppercase tracking-[0.15em] transition-all duration-300 ${
              scrolled
                ? "bg-[#0D2D54] text-white hover:bg-[#1A3D6B]"
                : "bg-white text-[#0D2D54] hover:bg-white/90"
            }`}
          >
            Order Now
          </Link>
        </nav>

        {/* Mobile hamburger */}
        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden transition-colors ${scrolled ? "text-[#0D2D54]" : "text-white"}`}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-[#E8E8E4] px-6 py-6 space-y-4"
        >
          {NAV_LINKS.map((link) => {
            const isHash = link.path.startsWith("/#");
            return isHash ? (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => { e.preventDefault(); handleNavClick(link, true); }}
                className="block text-base font-medium py-2 text-[#6E6E8A] cursor-pointer"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`block text-base font-medium py-2 ${
                  location.pathname === link.path
                    ? "text-[#0D2D54]"
                    : "text-[#6E6E8A]"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <Link
            to="/order"
            onClick={() => setMobileOpen(false)}
            className="block bg-[#0D2D54] text-white text-center px-6 py-3 font-medium hover:bg-[#1A3D6B] transition-all"
          >
            Order Now
          </Link>
        </div>
      )}
    </header>
  );
}
